import { Check } from 'lucide-react'

import { cn } from '@/utils'
import { type PickedUpSource, pagesReadOut, sentenceCase } from './moves'
import type { DocumentPage } from './types'

const SOURCES_SHOWN = 8

interface PickedUpProps {
  pages: DocumentPage[]
  sources: PickedUpSource[]
}

// What the column shows while the pages in hand come from more than one statement: the pick-up
// itself. Figures belong to one statement, so leaving a statement's own on screen would read as
// if they covered everything held.
export const PickedUp = ({ pages, sources }: PickedUpProps) => {
  const shown = sources.slice(0, SOURCES_SHOWN)
  const hidden = sources.length - shown.length

  return (
    <div data-picked-up className="flex flex-col gap-5 border-b p-4">
      <div className="space-y-1.5">
        <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-blue-700">
          <span className="flex size-4 items-center justify-center rounded-full bg-blue-600">
            <Check className="size-3 text-white" strokeWidth={3} />
          </span>
          Picked up
        </p>
        <p className="text-base font-medium text-foreground">
          {sentenceCase(pagesReadOut(pages))} from{' '}
          {sources.length.toLocaleString()} statements
        </p>
        <p className="text-xs text-ink-subtle">
          Figures belong to one statement at a time, so there are none for this
          set.
        </p>
      </div>

      <ul>
        {shown.map((source, index) => {
          const opensAnAccount = source.account !== shown[index - 1]?.account
          return (
            <li
              key={source.statementId}
              className={cn(opensAnAccount && index > 0 && 'pt-3')}
            >
              {opensAnAccount ? (
                <p className="text-sm font-medium text-foreground">
                  {source.account}
                </p>
              ) : null}
              <p className="text-sm text-foreground">
                {source.period}{' '}
                <span className="text-ink-subtle">
                  · {sentenceCase(pagesReadOut(source.pages))}
                </span>
              </p>
            </li>
          )
        })}
        {hidden > 0 ? (
          <li className="pt-3 text-sm text-ink-subtle">
            and {hidden.toLocaleString()} more{' '}
            {hidden === 1 ? 'statement' : 'statements'}
          </li>
        ) : null}
      </ul>
    </div>
  )
}
